/**
 * Where a crew member was going when the app sent them to sign in.
 *
 * The open-shift text links to /crew/jobs?tab=open (owner, Oct 1 2026). A
 * signed-out phone is sent to /login, and Login used to go to '/' after the
 * PIN — the shift they tapped for was lost. So the crew guard remembers the
 * page here and Login goes back to it.
 *
 * Only a crew page of this app is ever remembered or followed: a path that
 * starts with /crew/ and holds nothing but plain path and query characters —
 * never a full URL, never "//host", never a backslash. Anything else is
 * dropped, so this can never be turned into an open redirect. It lives in
 * sessionStorage: this tab only, gone when the tab closes, and taken (read
 * and cleared) by the one sign-in that uses it.
 */
const KEY = 'kam_return_to';

/** The path when it is a crew page of this app, else null. */
export function safeReturnPath(raw: unknown): string | null {
  if (typeof raw !== 'string') return null;
  const s = raw.trim();
  if (!s || s.length > 200 || s.includes('//')) return null;
  return /^\/crew\/[A-Za-z0-9/_-]*(\?[A-Za-z0-9_=&-]*)?$/.test(s) ? s : null;
}

/** Remember a crew page to come back to after signing in (anything else is ignored). */
export function rememberReturnTo(path: string) {
  const p = safeReturnPath(path);
  if (!p) return;
  try { sessionStorage.setItem(KEY, p); } catch { /* private mode — they land on their home page */ }
}

/** The remembered crew page, once: it is cleared as it is read. */
export function takeReturnTo(): string | null {
  let v: string | null = null;
  try { v = sessionStorage.getItem(KEY); sessionStorage.removeItem(KEY); } catch { /* storage blocked */ }
  return safeReturnPath(v);
}

/** Where to go right after a sign-in: the remembered crew page for a crew login, else home. */
export function afterSignIn(role: string | undefined | null): string {
  const back = takeReturnTo();
  return back && (role === 'crew' || role === 'crew_viewer') ? back : '/';
}
